import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { EntryType, LogEntry, Settings, UserSex } from '../types'
import {
  clearAllLogs,
  createLog,
  defaultSettings,
  fetchLogs,
  fetchSettings,
  persistSettings,
  removeLog,
  updateLog,
} from '../storage'

interface TrackerContextValue {
  logs: LogEntry[]
  settings: Settings
  loading: boolean
  syncing: boolean
  error?: string
  refresh: () => Promise<void>
  addLog: (entry: { type: EntryType; category: string; notes?: string; quantity?: number; photoData?: string }) => Promise<LogEntry>
  deleteLog: (id: string) => Promise<void>
  editLog: (id: string, updates: { category?: string; quantity?: number; notes?: string; type?: EntryType; performedAt?: string; photoData?: string | null }) => Promise<void>
  clearLogs: () => Promise<void>
  saveSettings: (next: Settings) => Promise<void>
  addCategoryFromLog: (type: EntryType, category: string) => Promise<void>
  userId: string
  profile?: UserDemographics
}

const TrackerContext = createContext<TrackerContextValue | undefined>(undefined)

export function TrackerProvider({ children, user, profile }: { children: React.ReactNode; user: string; profile?: UserDemographics }) {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [settings, setSettings] = useState<Settings>(defaultSettings)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string>()

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const cacheBust = Date.now().toString()
      const [remoteSettings, remoteLogs] = await Promise.all([
        fetchSettings(user, { cacheBust }),
        fetchLogs(user, { cacheBust }),
      ])
      setSettings(remoteSettings)
      setLogs(remoteLogs.sort((a, b) => +new Date(b.date) - +new Date(a.date)))
      setError(undefined)
    } catch (err) {
      console.error(err)
      setError((err as Error).message || 'Failed to load tracker data')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    refresh()
  }, [refresh])

  const runWithSync = useCallback(async <T,>(fn: () => Promise<T>) => {
    setSyncing(true)
    try {
      const result = await fn()
      setError(undefined)
      return result
    } catch (err) {
      console.error(err)
      setError((err as Error).message || 'Request failed')
      throw err
    } finally {
      setSyncing(false)
    }
  }, [])

  const addLog = useCallback(
    (entry: { type: EntryType; category: string; notes?: string; quantity?: number; photoData?: string }) =>
      runWithSync(async () => {
        const created = await createLog(user, entry)
        setLogs(prev => [created, ...prev])
        return created
      }),
    [runWithSync, user]
  )

  const deleteLog = useCallback(
    (id: string) =>
      runWithSync(async () => {
        await removeLog(user, id)
        setLogs(prev => prev.filter(l => l.id !== id))
      }),
    [runWithSync, user]
  )

  const editLog = useCallback(
    (id: string, updates: { category?: string; quantity?: number; notes?: string; type?: EntryType; performedAt?: string; photoData?: string | null }) =>
      runWithSync(async () => {
        const updated = await updateLog(user, id, updates)
        setLogs(prev => prev.map(l => (l.id === id ? updated : l)))
      }),
    [runWithSync, user]
  )

  const clearLogsHandler = useCallback(
    () =>
      runWithSync(async () => {
        await clearAllLogs(user)
        setLogs([])
      }),
    [runWithSync, user]
  )

  const saveSettingsHandler = useCallback(
    (next: Settings) =>
      runWithSync(async () => {
        const renames = getCategoryRenames(settings, next)
        await persistSettings(user, next)
        setSettings(next)

        const renameTargets = findLogRenames(logs, renames)
        if (!renameTargets.length) {
          return
        }
        const updates = new Map<string, LogEntry>()
        for (const target of renameTargets) {
          const updated = await updateLog(user, target.id, { category: target.nextCategory })
          updates.set(updated.id, updated)
        }
        if (updates.size) {
          setLogs(prev => prev.map(log => updates.get(log.id) ?? log))
        }
      }),
    [logs, runWithSync, settings, user]
  )

  const addCategoryFromLog = useCallback(
    async (type: EntryType, category: string) => {
      if (!category.trim()) return
      const next = {
        ...settings,
        categories: {
          ...settings.categories,
          [type]: settings.categories[type].includes(category) ? settings.categories[type] : [...settings.categories[type], category],
        },
      }
      await saveSettingsHandler(next)
    },
    [saveSettingsHandler, settings]
  )

  const value = useMemo<TrackerContextValue>(
    () => ({
      logs,
      settings,
      loading,
      syncing,
      error,
      refresh,
      addLog,
      deleteLog,
      editLog,
      clearLogs: clearLogsHandler,
      saveSettings: saveSettingsHandler,
      addCategoryFromLog,
      userId: user,
      profile,
    }),
    [addCategoryFromLog, addLog, clearLogsHandler, deleteLog, editLog, error, loading, logs, profile, refresh, saveSettingsHandler, settings, syncing, user]
  )

  return <TrackerContext.Provider value={value}>{children}</TrackerContext.Provider>
}

export function useTracker() {
  const ctx = useContext(TrackerContext)
  if (!ctx) {
    throw new Error('useTracker must be used inside TrackerProvider')
  }
  return ctx
}

type RenameMap = Partial<Record<EntryType, Record<string, string>>>

function getCategoryRenames(prev: Settings, next: Settings): RenameMap {
  const renames: RenameMap = {}
  const types: EntryType[] = ['did', 'ate', 'drank', 'other']
  for (const type of types) {
    const prevList = prev.categories?.[type] ?? []
    const nextList = next.categories?.[type] ?? []
    const len = Math.min(prevList.length, nextList.length)
    for (let i = 0; i < len; i++) {
      const prevValue = prevList[i] || ''
      const nextValue = nextList[i] || ''
      const prevTrimmed = prevValue.trim()
      const nextTrimmed = nextValue.trim()
      if (!prevTrimmed || !nextTrimmed) continue
      if (prevValue === nextValue) continue
      renames[type] = renames[type] || {}
      renames[type]![prevTrimmed] = nextValue
    }
  }
  return renames
}

function findLogRenames(logs: LogEntry[], renames: RenameMap) {
  const entries: { id: string; nextCategory: string }[] = []
  if (!Object.values(renames).some(map => map && Object.keys(map).length > 0)) {
    return entries
  }
  for (const log of logs) {
    const type = log.type as EntryType
    const renamedCategory = renames[type]?.[log.category.trim()]
    if (renamedCategory && renamedCategory !== log.category) {
      entries.push({ id: log.id, nextCategory: renamedCategory })
    }
  }
  return entries
}

export interface UserDemographics {
  age?: number
  sex?: UserSex
}
